import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import Modal from "react-modal";
import { Download, Mail, Trash2 } from "lucide-react";
import { updateGroupDatDetails } from "../../../services/emailGroupServices";
import whatsApp from "../../../assets/img/admin/whatsapp.png";

export default function EmailEditGroupModal({
  close,
  groupId,
  existingGroupDetails,
  onSuccess,
  isOpen,
}) {
  const modalRef = useRef(null);
  const nameInputRef = useRef(null);
  const [mode, setMode] = useState("manual");
  const [excelFile, setExcelFile] = useState(null);
  const [groupName, setGroupName] = useState(
    existingGroupDetails?.groupName || "Customers"
  );
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [users, setUsers] = useState(
    existingGroupDetails?.users?.map((user) => ({
      name: user.name,
      email: user.email,
      userAdditionType: "import",
    })) || []
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        close();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [close]);

  const handleDelete = (index) => {
    setUsers(users.filter((_, i) => i !== index));
  };

  const handleAddUser = () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Name and Email are required");
      return;
    }
    setUsers([...users, { name, email, userAdditionType: "manual" }]);
    setName("");
    setEmail("");
  };

  const handleUpdateGroup = async () => {
    if (!groupName.trim()) {
      toast.error("Group name is required");
      return;
    }

    try {
      if (mode === "manual") {
        const validUsers = users.filter(
          (user) => user.name.trim() !== "" && user.email.trim() !== ""
        );

        const payload = {
          groupName,
          users: validUsers,
        };

        await toast.promise(updateGroupDatDetails(groupId, payload), {
          loading: "Updating group...",
          success: "Group updated successfully",
          error: (err) => err.message || "Failed to update group",
        });

        onSuccess();
        return;
      }

      if (mode === "excel") {
        if (!excelFile) {
          toast.error("Please select an Excel file");
          return;
        }

        const formData = new FormData();
        formData.append("file", excelFile);
        formData.append("groupName", groupName);

        await toast.promise(updateGroupDatDetails(groupId, formData), {
          loading: "Uploading Excel...",
          success: "Group updated successfully from Excel",
          error: (err) => err.message || "Failed to update group from Excel",
        });

        onSuccess();
      }
    } catch (error) {
      console.error("Error updating group:", error);
      toast.error("Failed to update group. Please try again.");
    }
  };

  const validUsers = users.filter(
    (user) => user.name.trim() !== "" && user.email.trim() !== ""
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={close}
      ariaHideApp={false}
      contentLabel="Edit Group"
      overlayClassName="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50"
      className="bg-white animate-scale-in w-full max-w-[450px] rounded-[20px] p-6 relative mx-auto mt-20 outline-none shadow-lg"
    >
      <h2 className="text-center font-semibold text-black mb-3 text-lg">
        Update Group
      </h2>

      <button
        onClick={close}
        aria-label="Close"
        className="absolute right-5 top-3 text-black text-base font-bold focus:outline-none"
      >
        ✕
      </button>

      <div className="mb-3">
        <label className="block text-black text-sm font-medium mb-2">
          Group Name
        </label>
        <input
          type="text"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm bg-[#FAFAFA] focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      <div className="border border-[#E0E0E0] rounded-[10px] p-4">
        <label className="block text-black text-sm font-medium mb-3">
          Add Users
        </label>

        <div className="flex gap-5 mb-3">
          {["manual", "excel"].map((val) => (
            <label key={val} className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="mode"
                value={val}
                checked={mode === val}
                onChange={(e) => setMode(e.target.value)}
                className="appearance-none relative sr-only "
              />
              <div className="w-5 h-5 absolute rounded-full bg-[#d9d9d9] flex items-center justify-center">
                <div
                  className={`w-3 h-3 rounded-full transition ${
                    mode === val ? "bg-[#4362C1]" : "bg-[#C0C4D1]"
                  }`}
                />
              </div>
              <span className="ml-2 text-sm font-medium text-black">
                {val === "manual" ? "Manually" : "From Excel"}
              </span>
            </label>
          ))}
        </div>

        {mode === "manual" && (
          <>
            <div className="space-y-2 mb-3">
              <input
                ref={nameInputRef}
                type="text"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 placeholder:text-black rounded-lg text-sm bg-[#FAFAFA] focus:outline-none focus:ring-1 focus:ring-blue-500"
              />

              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500">
                  <Mail className="w-5 h-5" />
                </div>
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-3 py-3 border border-gray-300 placeholder:text-black rounded-lg text-sm bg-[#FAFAFA] focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            <button
              onClick={handleAddUser}
              className="w-full bg-[#4362C1] text-white h-10 rounded-lg text-sm font-semibold cursor-pointer mb-3"
            >
              Add User
            </button>
          </>
        )}

        {mode === "excel" && (
          <div className="mb-3">
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={(e) => setExcelFile(e.target.files?.[0] || null)}
              className="hidden"
              id="excel-upload"
            />
            <label
              htmlFor="excel-upload"
              className="flex justify-center items-center border rounded-[10px] gap-2 border-gray-300 px-6 h-10 bg-[#DFDCDC] cursor-pointer"
              aria-label="Import from Excel"
            >
              <Download className="w-4 h-4 text-black" />
              <span className="text-sm text-black truncate">
                {excelFile ? excelFile.name : "Import Excel"}
              </span>
            </label>
          </div>
        )}

        {mode === "manual" && validUsers.length > 0 && (
          <>
            <h3 className="text-center text-black text-sm font-medium mb-2 underline">
              Added Users List ({validUsers.length})
            </h3>
            <div className="max-h-32 overflow-y-auto space-y-0 custom-scrollbar">
              {validUsers.map((user, index) => (
                <div
                  key={index}
                  className="flex items-start justify-between py-2 border-b border-gray-200 last:border-0"
                >
                  <div className="flex items-start gap-3 flex-1">
                    <span className="text-sm text-black">{index + 1}.</span>
                    <div className="flex flex-col gap-y-1">
                      <span className="text-sm text-black">{user.name}</span>
                      <span className="text-sm text-gray-600">
                        {user.email}
                      </span>
                    </div>
                  </div>

                  <div className="w-[24px] flex justify-end">
                    {user.userAdditionType === "manual" ||
                    !user.userAdditionType ? (
                      <button
                        type="button"
                        aria-label={`Delete ${user.name}`}
                        onClick={() => handleDelete(index)}
                        className="cursor-pointer"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    ) : (
                      <span className="w-4 h-4 inline-block" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <button
          onClick={handleUpdateGroup}
          className="w-full bg-[#4362C1] text-white h-10 rounded-lg text-sm font-semibold cursor-pointer mt-4"
        >
          Update Group
        </button>
      </div>
    </Modal>
  );
}
